import React from "react";

import { Alert, Box, Button, Skeleton, Stack, Typography } from "@mui/material";

import type { VcsConnectionStatus } from "src/domains/application/hooks/useVcsConnectionStatus";

import { PIPELINE_STEPS } from "./pipeline-tab-helpers";

export interface PipelineEmptyStateProps {
  loading: boolean;
  error: string | null;
  orgMismatch: boolean;
  status?: string;
  vcsStatus: VcsConnectionStatus;
  onProvisionVcs?: () => void;
}

export function PipelineEmptyState({
  loading,
  error,
  orgMismatch,
  status,
  vcsStatus,
  onProvisionVcs,
}: PipelineEmptyStateProps): React.ReactElement | null {
  return (
    <>
      {loading && (
        <Stack spacing={1.5}>
          {PIPELINE_STEPS.map((step) => (
            <Stack
              key={step}
              direction="row"
              spacing={2}
              alignItems="flex-start"
            >
              <Skeleton
                variant="rounded"
                width={32}
                height={32}
                sx={{ borderRadius: 1.5, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="rounded"
                  height={52}
                  sx={{ borderRadius: 1.5 }}
                />
              </Box>
            </Stack>
          ))}
        </Stack>
      )}

      {error && !loading && (
        <Alert
          severity={orgMismatch ? "warning" : "info"}
          sx={{ borderRadius: 1.5 }}
        >
          {orgMismatch
            ? "This system belongs to a different organization. Switch organizations in the sidebar to view pipeline events."
            : status === "provisioning" || status === "vcs_provisioned"
              ? "VCS provisioning is in progress. Build and deploy will begin automatically once provisioning completes."
              : "Deployment pipeline events not available. This stack may not have been deployed yet."}
        </Alert>
      )}

      {vcsStatus.orgConnected && !vcsStatus.connected && !loading && !error && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 1.5 }}
          action={
            onProvisionVcs ? (
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={onProvisionVcs}
                sx={{ textTransform: "none" }}
              >
                Provision VCS
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2">
            VCS is connected at the organization level, but this stack does not
            have a provisioned repository. Provision a repo to enable builds and
            deploys.
          </Typography>
        </Alert>
      )}
    </>
  );
}
